import type { ArbitrageOpportunity } from '../types/opportunity';
import { getExchangeName } from '../types/opportunity';

/**
 * Check if an opportunity is executable based on exchange compatibility.
 *
 * Exchange Restrictions:
 * - Buy exchanges (can SEND funds): Binance, Bithumb
 * - Sell exchanges (can RECEIVE funds): Binance, Bithumb, Upbit
 *
 * Rationale: Upbit requires whitelisting to SEND funds, but receiving is fine.
 * We can sell on Upbit (receive funds there), but cannot buy from Upbit (send funds from there).
 */
export function isExecutableOpportunity(opportunity: ArbitrageOpportunity): boolean {
  // Get exchange names
  const buyExchange = getExchangeName(opportunity.buy_exchange);
  const sellExchange = getExchangeName(opportunity.sell_exchange);

  // Buy exchanges: can send funds (no whitelisting required)
  const buyExchanges = ['Binance', 'Bithumb'];

  // Sell exchanges: can receive funds (Upbit can receive but not send)
  const sellExchanges = ['Binance', 'Bithumb', 'Upbit'];

  // Check buy exchange can send AND sell exchange can receive
  const canBuyFrom = buyExchanges.includes(buyExchange);
  const canSellTo = sellExchanges.includes(sellExchange);

  return canBuyFrom && canSellTo;
}

/**
 * Strategy type definitions (matches Rust backend enum)
 */
export type StrategyType = 'Cross' | 'HedgedCross' | 'StandbyHedgedCross' | 'AdaptiveHedgedCross' | 'StandbyHedged';

/**
 * Get available strategies based on futures data availability.
 *
 * - Cross Strategy: Always available (no futures data needed)
 * - Hedged strategies: Require futures orderbook AND mark price data
 * - StandbyHedged: Same exchange, no transfer (requires futures data)
 *
 * Core Principle: "Can't hedge without futures market data"
 */
export function getAvailableStrategies(hasFuturesData: boolean): StrategyType[] {
  if (!hasFuturesData) {
    // Only Cross strategy available (can't hedge without futures)
    return ['Cross'];
  }

  // All 5 strategies available when futures data present
  return ['Cross', 'HedgedCross', 'StandbyHedgedCross', 'AdaptiveHedgedCross', 'StandbyHedged'];
}

/**
 * Calculate estimated profit based on opportunity and budget.
 * This matches the calculation in ProfitCalculationTooltip.
 *
 * Formula: net_sell_revenue - total_buy_cost_in_sell_quote
 */
export function calculateEstimatedProfit(
  opportunity: ArbitrageOpportunity,
  budget: number
): number {
  // Use computed buy/sell prices from opportunity
  const buyPrice = opportunity.buy_price || 0;
  const sellPrice = opportunity.sell_price || 0;

  if (buyPrice === 0 || sellPrice === 0) {
    return 0;
  }

  // Get fees from opportunity (already calculated)
  const buyFee = opportunity.buy_fee || 0;
  const sellFee = opportunity.sell_fee || 0;

  // Calculate volume based on budget (in buy quote currency)
  const volumeBeforeFees = budget / buyPrice;

  // After buying with fee, we receive less crypto
  const volumeAfterBuyFee = volumeBeforeFees * (1 - buyFee);

  // Calculate total buy cost
  const totalBuyCost = budget;

  // Calculate sell revenue (using volume after buy fee)
  const grossSellRevenue = volumeAfterBuyFee * sellPrice;

  // Apply sell fee
  const netSellRevenue = grossSellRevenue * (1 - sellFee);

  // Convert buy cost to sell quote currency for comparison
  const totalBuyCostInSellQuote = opportunity.quote_conversion_rate !== 1.0
    ? totalBuyCost * opportunity.quote_conversion_rate
    : totalBuyCost;

  // Net profit in sell quote currency
  return netSellRevenue - totalBuyCostInSellQuote;
}
